import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

export interface ProviderView {
  id: string;
  adapter: string;
  label: string;
  isActive: boolean;
  configuredKeys: Record<string, string>;
  lastSyncAt: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
}

export interface ProviderTestResult {
  ok: boolean;
  message: string;
  vehicles: { externalId: string; plate: string | null; name: string }[];
}

export interface LinkReport {
  linked: { vehicleId: string; fleetNo: string; plate: string; externalId: string }[];
  unmatchedOurs: { vehicleId: string; fleetNo: string; plate: string }[];
  /** Бусовете във Frotcom без двойник при нас — кандидатите за внасяне. */
  unmatchedTheirs: {
    externalId: string;
    plate: string | null;
    name: string;
    make?: string | null;
    model?: string | null;
    lastSeenAt?: string | null;
  }[];
}

export interface ImportReport {
  created: { vehicleId: string; fleetNo: string; plate: string; externalId: string }[];
  linked: { vehicleId: string; fleetNo: string; plate: string; externalId: string }[];
  removed: { vehicleId: string; fleetNo: string; plate: string }[];
  skipped: { externalId: string; reason: string }[];
}

export function useProviders() {
  return useQuery({ queryKey: ['telematics', 'providers'], queryFn: () => api.get<ProviderView[]>('/telematics/providers') });
}

export function useTestProvider() {
  return useMutation({
    mutationFn: (body: { adapter: string; config: Record<string, string> }) =>
      api.post<ProviderTestResult>('/telematics/providers/test', body),
  });
}

export function useSaveProvider() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string; adapter: string; label: string; config?: Record<string, string>; isActive?: boolean }) =>
      id ? api.post<{ id: string }>(`/telematics/providers/${id}`, body) : api.post<{ id: string }>('/telematics/providers', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['telematics'] }),
  });
}

export function useLinkVehicles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<LinkReport>(`/telematics/providers/${id}/link`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['telematics'] });
      void qc.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
}

export function usePollNow() {
  return useMutation({ mutationFn: () => api.post<unknown>('/telematics/poll') });
}

/** Внасяне на бусовете от доставчика — избраните стават наши, примерните по желание се махат. */
export function useImportVehicles(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { vehicles: { externalId: string; fleetNo: string }[]; removeVehicleIds: string[] }) =>
      api.post<ImportReport>(`/telematics/providers/${providerId}/import`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['telematics'] });
      void qc.invalidateQueries({ queryKey: ['vehicles'] });
      void qc.invalidateQueries({ queryKey: ['board'] });
      void qc.invalidateQueries({ queryKey: ['anomalies'] });
      void qc.invalidateQueries({ queryKey: ['vignettes'] });
    },
  });
}

/** Бусовете при Frotcom срещу нашите — без запис; за страницата, щом е свързан. */
export function useProviderVehicles(providerId: string | null) {
  return useQuery({
    queryKey: ['telematics', 'vehicles', providerId],
    queryFn: () => api.get<LinkReport>(`/telematics/providers/${providerId}/vehicles`),
    enabled: Boolean(providerId),
    staleTime: 60_000,
    retry: false,
  });
}

/** ─── Сверка с доставчика ────────────────────────────────────────────── */

export type DiagnoseVerdict =
  | 'ok'
  | 'not_linked'
  | 'privacy'
  | 'no_gps'
  | 'no_signal'
  | 'ours_stale'
  | 'mismatch'
  | 'frotcom_inconsistent';

export interface DiagnoseRow {
  externalId: string;
  frotcomPlate: string | null;
  frotcomVehicleId: number | null;
  frotcomAssetId: number | null;
  fleetNo: string | null;
  plate: string | null;
  frotcom: {
    lat: number | null;
    lon: number | null;
    at: string | null;
    source: 'feed' | 'list' | null;
    speed: number | null;
    lastCommunication: string | null;
    odometerGps: number | null;
    odometerCanbus: number | null;
    useCanbusMileage: boolean | null;
    noGpsPct: number | null;
    feedPoints: number;
    listLat: number | null;
    listLon: number | null;
    listAt: string | null;
    lastPosition: { lat: number; lon: number } | null;
  };
  ours: {
    lat: number | null;
    lon: number | null;
    at: string | null;
    speed: number | null;
    odometerKm: number | null;
    lastSeenAt: string | null;
    motionState: string | null;
  } | null;
  distanceM: number | null;
  listVsFeedM: number | null;
  lagSec: number | null;
  verdict: DiagnoseVerdict;
}

export interface DiagnoseReport {
  provider: string;
  checkedAt: string;
  frotcomVehicles: number;
  lastSyncAt: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
  vehicles: DiagnoseRow[];
  oursWithoutFrotcom: Array<{ vehicleId: string; fleetNo: string; plate: string; unitId: string | null }>;
  ingestion: { pollIntervalMs: number; pausedProviders: number; failingProviders: number };
}

/** Тегли се само при отворен прозорец — всяка сверка е заявки към Frotcom. */
export function useProviderDiagnose(providerId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['telematics', 'diagnose', providerId],
    queryFn: () => api.get<DiagnoseReport>(`/telematics/providers/${providerId}/diagnose`),
    enabled: Boolean(providerId) && enabled,
    staleTime: 0,
    retry: false,
  });
}
